import { useEffect, useState } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import {
    listFormsByCompany,
    listFormTemplatesByGroup,
    createFormTemplate,
    deleteFormTemplate,
} from '../../graphql/companyResourceQueries';
import { Btn, BtnIconOnly, IconClose } from '../ui';

interface AssignFormsModalProps {
    isOpen: boolean;
    onClose: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    group: any;
    companyId: string;
    onSaved: () => void;
}

export const AssignFormsModal = ({ isOpen, onClose, group, companyId, onSaved }: AssignFormsModalProps) => {
    const { companyOwner } = useUserContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [allForms, setAllForms] = useState<any[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [existingTemplates, setExistingTemplates] = useState<any[]>([]);
    const [selectedInspectionIds, setSelectedInspectionIds] = useState<Set<string>>(new Set());
    const [selectedDailyIds, setSelectedDailyIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen && group) loadData();
    }, [isOpen, group]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [forms, templates] = await Promise.all([
                listFormsByCompany(companyId),
                listFormTemplatesByGroup(group.id),
            ]);
            setAllForms(forms);
            setExistingTemplates(templates);

            // Pre-select forms that already have templates in this group
            const inspIds = new Set<string>();
            const dailyIds = new Set<string>();
            for (const tmpl of templates) {
                const matchingForm = forms.find((f: any) => f.name === tmpl.templateName);
                if (matchingForm) {
                    if (tmpl.dailyServiceLogForm) dailyIds.add(matchingForm.id);
                    else inspIds.add(matchingForm.id);
                }
            }
            setSelectedInspectionIds(inspIds);
            setSelectedDailyIds(dailyIds);
        } catch (err) {
            console.error('Error loading forms data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const inspectionForms = allForms.filter(f => !f.dailyServiceLogForm);
    const dailyForms = allForms.filter(f => f.dailyServiceLogForm);

    const toggleInspection = (formId: string) => {
        setSelectedInspectionIds(prev => {
            const next = new Set(prev);
            if (next.has(formId)) next.delete(formId); else next.add(formId);
            return next;
        });
    };

    const toggleDaily = (formId: string) => {
        setSelectedDailyIds(prev => {
            const next = new Set(prev);
            if (next.has(formId)) next.delete(formId); else next.add(formId);
            return next;
        });
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            const selectedInspForms = inspectionForms.filter(f => selectedInspectionIds.has(f.id));
            const selectedDlyForms = dailyForms.filter(f => selectedDailyIds.has(f.id));
            const allSelectedNames = new Set([
                ...selectedInspForms.map((f: any) => f.name),
                ...selectedDlyForms.map((f: any) => f.name),
            ]);

            // Delete templates that are no longer selected (only ones created by this company)
            const templatesToDelete = existingTemplates.filter(
                (tmpl: any) => !allSelectedNames.has(tmpl.templateName) && tmpl.originalCompanyId === companyId
            );
            for (const tmpl of templatesToDelete) {
                await deleteFormTemplate(tmpl.id);
            }

            // Create templates for newly selected forms
            const existingNames = new Set(existingTemplates.map((t: any) => t.templateName));
            const ownerId = companyOwner?.id || '';

            for (const form of selectedInspForms) {
                if (!existingNames.has(form.name)) {
                    await createFormTemplate({
                        templateName: form.name,
                        templateData: form.formData,
                        createdByCompanyOwnerId: ownerId,
                        originalCompanyId: companyId,
                        formTemplateGroupID: group.id,
                        inspectionForm: true,
                        dailyServiceLogForm: false,
                    });
                }
            }

            for (const form of selectedDlyForms) {
                if (!existingNames.has(form.name)) {
                    await createFormTemplate({
                        templateName: form.name,
                        templateData: form.formData,
                        createdByCompanyOwnerId: ownerId,
                        originalCompanyId: companyId,
                        formTemplateGroupID: group.id,
                        inspectionForm: false,
                        dailyServiceLogForm: true,
                    });
                }
            }

            onSaved();
            onClose();
        } catch (err) {
            console.error('Error assigning forms:', err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="cr-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="cr-modal cr-modal--wide" onClick={e => e.stopPropagation()}>
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">Assign Forms to {group.name}</h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {isLoading ? (
                        <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading forms...</p>
                    ) : (
                        <>
                            <p className="cr-field-label" style={{ fontWeight: 600, marginBottom: 8 }}>All Inspection Forms:</p>
                            {inspectionForms.length === 0 ? (
                                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 16 }}>No inspection forms found.</p>
                            ) : (
                                <div style={{ marginBottom: 20 }}>
                                    {inspectionForms.map((form: any) => (
                                        <label key={form.id} className="cr-checkbox-label" style={{ display: 'flex', marginBottom: 6 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedInspectionIds.has(form.id)}
                                                onChange={() => toggleInspection(form.id)}
                                            />
                                            {form.name}
                                        </label>
                                    ))}
                                </div>
                            )}

                            <p className="cr-field-label" style={{ fontWeight: 600, marginBottom: 8 }}>All Daily Service Forms:</p>
                            {dailyForms.length === 0 ? (
                                <p style={{ fontSize: '0.82rem', color: '#6b7280' }}>No daily service forms found.</p>
                            ) : (
                                <div>
                                    {dailyForms.map((form: any) => (
                                        <label key={form.id} className="cr-checkbox-label" style={{ display: 'flex', marginBottom: 6 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedDailyIds.has(form.id)}
                                                onChange={() => toggleDaily(form.id)}
                                            />
                                            {form.name}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} buttonDisabled={isSaving} />
                    <Btn
                        variant="primary"
                        buttonText={isSaving ? 'Saving...' : 'Submit'}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={isSaving || isLoading}
                    />
                </div>
            </div>
        </div>
    );
};
